import React, {useEffect, useMemo, useRef, useState} from 'react'
import User, {talentUpdateRequest} from "../../models/User";
import {ProgressBar} from "react-step-progress-bar";
import ReviewStep from "./steps/ReviewStep";
import ResumeUploadModal from "./ResumeUploadModal";
import ApplicationSideNavBar from "./ApplicationSideNavBar";
import {matchPath, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import {useMutation} from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import PageNotFound from "../../components/errors/PageNotFound";
import {BioStep} from "./steps";
import {Modal} from "antd";
import {CheckCircleFilled} from "@ant-design/icons";
import ReadyToSubmitProfileModal from "./ReadyToSubmitProfileModal";

const TalentApplication = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const routeRef = useRef(null);
    const resumeModalRef = useRef(null);

    const {auth, update: updateAuth, talentUpdate, appropriate, logout} = useAuth();

    const [isDisabled, setIsDisabled] = useState(false);

    const [stepSubmitDisabled, setStepSubmitDisabled] = useState(false);


    const isAdminEditing = auth.canManageClientResourceTalent() && !!props.user;

    const requestEndpoint = useMemo(() => {
        if (!isAdminEditing || !props.user?.id || !auth?.hasSupplierClientOrg()) {
            return '/api/sessions/update'
        }
        return `/api/clients/${auth.supplierOrg()?.id}/users/${props.user.id}/update`
    }, [auth, isAdminEditing]);

    const authTalentUser = useMemo(() => {
        let user = {...(props.user || auth || {})};
        if (user.phone) {
            let phoneNumber = user.phone.replace(/\D/g, '');
            if (phoneNumber?.length === 10 && !user.phone.includes('+')) {
                user.phone = "+1 " + user.phone;
            }
        }
        return User.make({...user})
    }, [props.user, auth, isDisabled]);

    const [talentUser, setTalentUser] = useState(() => authTalentUser);

    const [activeSteps, setActiveSteps] = useState(() => talentUser?.currentProfileSteps());

    const profileProgress = useMemo(() => auth?.profileProgress(), [auth, isDisabled])
    const [lastSavedStep, setLastSavedStep] = useState(() => talentUser?.currentApplicationStep())

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(activeSteps, (step, index) => {
            return  location.pathname.includes(step.getPath(!isAdminEditing ? '/talent/application' : ''))
        })
    }, [location?.pathname, activeSteps]);

    const currentStep = useMemo(() => {
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const nextStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex + 1]
        }
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const prevStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex - 1]
        }

        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    useEffect(() => {
        if (!talentUser || currentStepIndex !== -1) {
            return;
        }
        if (!lastSavedStep) {
            // do something else??
        }

        return goToStep(lastSavedStep, true)
    }, [talentUser, currentStepIndex])

    const isReviewing = () => {
        return location.state?.fromReview
    }

    const saveAndExitInvalidForm = async() => {
        if (TB.AlertIsShowing) {
            await TB.event('Alert@hide');
            await TB.wait(400);
            console.log('saveAndExitInvalidForm', 'WAITING')
        }
        let exitAnyways = await TB.sysAlert('talent.application_save_and_exit_button_clicked');

        // console.log('saveAndExitInvalidForm', {exitAnyways})
        if (exitAnyways) {
            if (isAdminEditing) {

                console.log('saveAndExitInvalidForm', "/supplier/dashboard")
                return navigate('/supplier/dashboard', {replace: true})
            }
            // console.log('saveAndExitInvalidForm', talentUser?.hasCompletedProfile(), appropriate())
            if (talentUser?.hasCompletedProfile()) {
                return navigate('/talent/dashboard')
            }

            return logout();
        }
    }

    const goToStep = (step, replace = false) => {
        let route = {
            ...location,
            pathname: step.getPath(!isAdminEditing ? '/talent/application' : '.')
        }
        if (step === BioStep &&  talentUser?.resumeDocument?.() && !auth.generated_account_data && !talentUser.isApplicationApproved()) {
            route.search = '?importResume=1'
        }
        if (step === ReviewStep && !talentUser?.hasCompletedApplication()) {
            return TB.alert('warning', 'Application Not Finished',
                'You haven\'t finished all the required steps. Please complete the remaining steps in order to submit your application.',  [{title: 'Ok'}]
            )
        }
        if (replace) {
            return navigate(route, {replace: true});
        }
        return navigate(route)
    }

    // const toggleResumeModal = async(show = false) => {
    //     const search = show ? '?uploadResume=true' : '';
    //
    //     navigate({pathname: location.pathname, search}, {replace: true});
    //
    //     if (!show) {
    //         await TB.wait(1500);
    //
    //         await resumeModalRef?.current?.resetState();
    //     }
    // }

    const toggleDisabled = (submitDisabled) => {
        return setStepSubmitDisabled(submitDisabled)
    }

    const updateTalentApplicationMutation = useMutation(
        ['talentUpdate', talentUser.id],
            params => talentUpdateRequest(params, requestEndpoint)
    )

    const onUpdateTalent = async(updatedTalent) => {
        let user = updatedTalent;
        if (!isAdminEditing || talentUser.id === auth.id) {
            user = await updateAuth(updatedTalent)
        } else if (props.onUpdateTalent) {
            user = await props.onUpdateTalent(updatedTalent)
        }
        return setTalentUser(user);
    };

    const isEditingInterviewStep = useMemo(() => {

        if (!location.pathname.includes('/video-interview')) {
            return false;
        }

        const match = matchPath({path: 'talent/application/video-interview/*'},location.pathname);
        console.log('match', match)
        return match?.params['*']?.length > 0;
    }, [location.pathname, isDisabled]);

    const onSubmit = async(params, exitApplication = false, skipGoToNext = false) => {
        // if (stepSubmitDisabled && exitApplication) {
        //     let exitNow = await TB.sysAlert('talent.application_save_and_exit_button_clicked');
        //     if (exitNow) return logout();
        //     else return;
        // }

        try {
            let invalidField = false;
            if (routeRef?.current
                && TB.isFunction(routeRef.current.invalidFields)) {
                invalidField = await routeRef.current?.invalidFields();
            }

            if (invalidField) {

                if (exitApplication) {
                    return saveAndExitInvalidForm();
                }
                return
            }
        } catch (e) {
            console.warn('ERROR: onSubmitValidateCheck', e);
        }


        let user = talentUser;

        let completedStep = await currentStep?.hasCompleted(
            User.make({
                ...params
            })
        );

        if ((completedStep || user.getFullProfileStepsForTalent().includes(currentStep)) && params) {


            let validated = await currentStep?.getValidProps(
                User.make({
                    ...params
                }), skipGoToNext
            );

            if (!validated) {
                await TB.event("TalentApp@Submit");
                if (exitApplication) {

                    return saveAndExitInvalidForm();
                }
                else return;
            }
            await setIsDisabled(true)

            try {
                user = await updateTalentApplicationMutation.mutateAsync(validated);

            } catch (e) {
                user = false;
                console.warn('ERROR: onSubmit', e);

                if (TB.auth().TalentUpdateRequest?.didFail &&
                    (TB.auth().TalentUpdateRequest?.errorType === TB.Request.ErrorTooLong ||
                        TB.auth().TalentUpdateRequest?.errorType === TB.Request.ErrorLocal)) {

                    await setIsDisabled(false)

                    return await TB.alert('error',
                        TB.trans('api.system_maintenance.title'),
                        TB.trans('api.system_maintenance.message', {code: 'XXX'}), [
                            {
                                title: 'Okay',
                            }
                        ]
                    )
                }
            }

            if (!user) {
                /*
                * Update form with server errors
                * */
                // console.log('onSubmit', TB.auth().TalentUpdateRequest)
                if (await TB.auth().talentHasInputResponseError()) {
                    await routeRef.current?.handleInputResponseError(TB.auth().TalentUpdateRequest);
                }


                if (exitApplication) return saveAndExitInvalidForm();

                return setIsDisabled(false)
            }
        }

        if (!completedStep && !user.getFullProfileStepsForTalent?.().includes(currentStep)) {
            let validated = await currentStep.getValidProps(
                User.make({
                    ...params
                }), skipGoToNext
            );

            if (!validated) {
                await TB.event("TalentApp@Submit");
                if (exitApplication) return saveAndExitInvalidForm();
                else return;
            }
        }
        await onUpdateTalent(user);

        if (isReviewing()) {
            await setIsDisabled(false)

            return goToStep(ReviewStep, true)
        }

        if (exitApplication) {
            await setIsDisabled(false);

            if (user.hasCompletedProfile()) {
                return navigate(appropriate())
            }
            return logout();
        }

        if (skipGoToNext) {
            await setIsDisabled(false);
            console.log('skipppp', user)
            return user;
        }

        if (nextStep) {
            if (currentStepIndex > activeSteps.indexOf(lastSavedStep)) {
                await setLastSavedStep(() => currentStep)
            }
            await setIsDisabled(false)

            await goToStep(nextStep);

            if (talentUser?.isApplicationApproved() && talentUser.isProfileReadyForApproval()) {
                let params = new URLSearchParams(searchParams)
                params.set('submitForApproval', '1');
                setSearchParams(params);
            }
            return;
        }

        await setIsDisabled(false)

        let redirect;

        if (user.isTalentSupplierResource?.() && user?.isApplicationApproved() && currentStep === ReviewStep) {
            await setLastSavedStep(() => user?.currentApplicationStep())
            await setActiveSteps(() => user?.currentProfileSteps())
            return await TB.alert('success', 'Supplier Profile Updated Successfully',
                'Your profile is now completed. You can edit this information at any time', [
                    {title: 'Go to dashboard', click: () => navigate('/talent/dashboard', {replace: true})},
                    {
                        title: 'Continue editing', click: () => window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                        })
                    }
                ]
            );
        }
        if (!TB.auth().isAppropriate(location)) {
            redirect = TB.auth().appropriate();
        }

        if (redirect) {
            navigate(redirect)
        }

        if (talentUser?.isApplicationApproved() && talentUser.isProfileReadyForApproval()) {
            let params = new URLSearchParams(searchParams)
            params.set('submitForApproval', '1');
            setSearchParams(params);
        }
    }

    // if (!currentStep) {
        // <Navigate to={{
        //     ...location,
        //     pathname: `${!isAdminEditing ? '/talent/application' : '.'}${(lastSavedStep || ApplicationStartStep).path}`,
        // }} replace/>
        // return null
    // }

    // Fix for showing progress text when percent is small, i.e 10% round up to 15
    let progressPercent = profileProgress > 0 ? Math.max(profileProgress, 15) : profileProgress;

    let isLastStep = currentStepIndex === activeSteps.length - 1,
        lastStepRemaining = talentUser.lastStepRemaining();

    const isReviewStep = location.pathname.endsWith('/review');
    // const isInterviewStep = location.pathname.includes('/video-interview');


    return (
        <>
            {currentStep?.StepTitle && (
                <Helmet>
                    <title>{String(currentStep?.StepTitle)} - Profile | Wripple</title>
                </Helmet>
            )}

            <ReadyToSubmitProfileModal onSubmitProfileApproval={params => {
                setTalentUser(User.make({
                    ...talentUser,
                    ...params
                }))
            }}/>

            <div className={isEditingInterviewStep ? '' : ("talent-application-container " + (!currentStep?.showFormButtons() && 'review-application'))}>
                {!isEditingInterviewStep && (
                    <div className="talent-application-progress">
                        {(!isAdminEditing || talentUser.id === auth.id) ? (
                            <React.Fragment>
                                <ProgressBar
                                    filledBackground={`linear-gradient(135deg, ${TB.config('color.success')} 0%, ${TB.config('color.success-darker')} 100%)`}
                                    height={30}
                                    percent={progressPercent}
                                    unfilledBackground={TB.config('color.backgroundGray')}/>
                                <p className="talent-app-progress-title font-second"
                                   style={{right: `${100 - progressPercent}%`}}>

                                    {`${profileProgress}%`}
                                    <span
                                        className="hidden font-secondary sm:flex">&nbsp;{TB.trans('talent.talent_application.progress_title')}</span>
                                </p>
                            </React.Fragment>
                        ) : (
                            <div className="h-[30px] self-stretch bg-danger flex flex-col items-center justify-center">
                                <p className="talent-app-progress-title talent-supplier-resource text-white text-center">
                                    <span className="font-medium">NOTE: Currently editing Talent Resource Profile
                                        for:&nbsp;</span>
                                    <span className="font-bold text-black">{talentUser.fullName()}</span>
                                </p>
                            </div>
                        )}
                    </div>
                )}
                <div className={isEditingInterviewStep ? '' : "talent-application-form-container"}>

                    {!isEditingInterviewStep && currentStep && (currentStep.StepInfo || currentStep === ReviewStep) && (
                        <ApplicationSideNavBar
                            {...props}
                            steps={activeSteps}
                            disabled={isDisabled}
                            currentStep={currentStep}
                            currentStepIndex={currentStepIndex}
                            onStepNavigate={step => (!isDisabled && goToStep(step))}/>
                    )}


                    <div className={isEditingInterviewStep ? '' : "w-full lg:w-8/12 xl:w-9/12 pt-6 md:pt-0"}>

                        <Routes>
                            <Route index element={<Navigate
                                to={{...location, pathname: TB.replace(lastSavedStep?.path, '/', '')}} replace/>}/>
                            {activeSteps.map(Step => (
                                <Route key={Step.path} path={TB.replace(Step.path, '/', '') + '/*'} element={(
                                    <Step
                                        ref={routeRef}
                                        stepNeeded={lastStepRemaining}
                                        user={talentUser}
                                        authUser={authTalentUser}
                                        isEditingInterviewStep={isEditingInterviewStep}
                                        onEditingChildForm={toggleDisabled}
                                        isAdminEditing={!!isAdminEditing}
                                        handleEditing={toggleDisabled}
                                        disabled={isDisabled}
                                        onSubmitChild={(params) => onSubmit(params, false, true)}
                                        onSubmit={onSubmit}
                                        onChange={params => {
                                            console.log('onchangetalentapp', params)
                                            setTalentUser(User.make({
                                                ...talentUser,
                                                ...params
                                            }))
                                        }}
                                    />
                                )}/>
                            ))}
                            {/*<Navigate to={{...location, pathname: TB.replace(lastSavedStep?.path, '/', '')}} replace/>*/}
                            <Route path={'*'} element={<PageNotFound exitText="Back to Application" exitLink={{...location, pathname: TB.replace(lastSavedStep?.path, '/', '')}} />}/>

                        </Routes>
                    </div>
                </div>

                {!isEditingInterviewStep && currentStep?.showFormButtons() && (
                    <div className="talent-app-footer">
                        <div className="lg:w-9/12 xl:w-10/12 ml-auto flex">

                            <div className="flex-grow flex flex-row items-center flex-wrap gap-4">
                                {(!talentUser.hasCompletedProfile() || currentStepIndex > 0) && (
                                    <button
                                        type="button"
                                        onClick={() => goToStep(prevStep, true)}
                                        disabled={isDisabled || stepSubmitDisabled || currentStepIndex === 0}
                                        className="btn btn-outline-dark">
                                        Back
                                    </button>
                                )}
                                {talentUser.hasCompletedProfile() && (
                                    <div className="flex-row items-center gap-2 hidden md:flex">
                                        <CheckCircleFilled className="-mt-0.5 text-[#22C55E] text-xs"/>
                                        <p className="text-[#22C55E] text-xs">
                                            Profile Complete
                                        </p>
                                    </div>
                                )}
                                <button type="button"
                                        disabled={isDisabled || stepSubmitDisabled}
                                        onClick={() => onSubmit(talentUser, true)}
                                        className="ml-[unset] md:ml-auto btn btn-outline-dark">
                                    {location?.state?.fromReview ?
                                        'Back to Review' :
                                        'Save & Exit'
                                    }
                                </button>
                                {!isLastStep && (
                                    <button
                                        type="button"
                                        disabled={isDisabled || stepSubmitDisabled}
                                        onClick={() => onSubmit(talentUser)}
                                        className={TB.mapClass("btn", {
                                            "btn-primary": !talentUser.isProfileReadyForApproval(),
                                            "btn-outline-primary text-primary font-semibold": talentUser.isProfileReadyForApproval()
                                        })}>
                                        {(isLastStep) ? 'Submit' :
                                            (isReviewing() ? 'Update' :
                                                    'Continue'
                                            )}
                                    </button>
                                )}
                                {!isReviewing() && talentUser.isProfileReadyForApproval() && (
                                    <button
                                        type="button"
                                        disabled={isDisabled || stepSubmitDisabled || searchParams.has('submitForApproval')}
                                        onClick={() => {

                                            if (!searchParams.has('submitForApproval') && !isDisabled) {
                                                let params = new URLSearchParams(searchParams);
                                                params.set('submitForApproval', '1');
                                                setSearchParams(params);
                                            }
                                        }}
                                        className="flex-1 md:flex-grow-0 btn btn-primary whitespace-no-wrap min-w-[unset]">
                                        Submit for Approval →
                                    </button>
                                )}
                            </div>
                        </div>
                        {/*<div className="md:w-full save-and-exit-mobile">*/}
                        {/*    <button type="button"*/}
                        {/*            disabled={isDisabled || stepSubmitDisabled}*/}
                        {/*            onClick={() => onSubmit(talentUser, true)}*/}
                        {/*            className="btn btn-link">*/}
                        {/*        {location?.state?.fromReview ?*/}
                        {/*            'Back to Review' :*/}
                        {/*            'Save & Exit'*/}
                        {/*        }*/}
                        {/*    </button>*/}
                        {/*</div>*/}
                    </div>
                )}

                <Modal
                    okText={null}
                    zIndex={199}
                    footer={null}
                    cancelText={null}
                    title={null}
                    // className="schedule-meeting-modal"
                    open={!talentUser.isApplicationApproved() && searchParams?.has('importResume')}
                    closeable={false}
                    maskClosable={false}
                    style={{top: 20}}
                    destroyOnClose={true}
                    width={900}
                    closeIcon={<div className="hidden pointer-events-none"/>}
                    // onCancel={}
                >
                    <ResumeUploadModal
                        onRequestClose={() => {
                            navigate({
                                pathname: location.pathname,
                            }, {replace: true})
                        }}
                                onUpdate={async (props = {}) => {
                                    let user = false;
                                    await setIsDisabled(true)

                                    try {
                                        user = await updateTalentApplicationMutation.mutateAsync(props);

                                    } catch (e) {
                                        console.warn('ERROR', e)
                                        user = false;
                                    }
                                    if (!user) {
                                        await setIsDisabled(false)

                                        return TB.showServerErrorToast();
                                    }

                                    if (user) {
                                        await user.syncResumeImportLocalStorage(props);


                                        // let params = new URLSearchParams(searchParams);
                                        // params.delete('importResume')
                                        // await setSearchParams(params)

                                        await onUpdateTalent(user);

                                        await setIsDisabled(false)
                                        navigate({
                                            pathname: location.pathname,
                                        }, {replace: true})
                                        await TB.sysAlert('talent.resume_imported_success')
                                    }
                                }}
                                disabled={isDisabled}
                    />

                </Modal>
                {/*<Modal*/}
                {/*    onCloseAction={() => toggleResumeModal(false)}*/}
                {/*    onUploadSuccess={response => {*/}
                {/*        console.log('onUploadSuccess', response);*/}
                {/*    }}*/}
                {/*    modalClass="resume-upload-modal"*/}
                {/*    isVisible={queryParams.has('uploadResume')}>*/}

                {/*    <ResumeUploadModal*/}
                {/*        ref={resumeModalRef}*/}
                {/*        onRequestClose={() => toggleResumeModal(false)}*/}
                {/*        onUpdate={async (props = {}) => {*/}
                {/*            let user = false;*/}
                {/*            await setIsDisabled(true)*/}

                {/*            try {*/}
                {/*                let updates = {};*/}
                {/*                for (let [propName, value] of Object.entries(props)) {*/}
                {/*                    updates[propName] = value || [];*/}

                {/*                    if (talentUser[propName]?.length) {*/}
                {/*                        updates[propName] = [*/}
                {/*                            ...updates[propName],*/}
                {/*                            ...talentUser[propName],*/}
                {/*                        ];*/}
                {/*                    }*/}
                {/*                }*/}
                {/*                user = await updateTalentApplicationMutation.mutateAsync(updates);*/}

                {/*            } catch (e) {*/}
                {/*                user = false;*/}
                {/*            }*/}
                {/*            if (!user) {*/}
                {/*                await setIsDisabled(false)*/}

                {/*                return TB.showServerErrorToast();*/}
                {/*            }*/}

                {/*            if (user) {*/}
                {/*                await toggleResumeModal(false);*/}

                {/*                await onUpdateTalent(user);*/}

                {/*                await setIsDisabled(false)*/}

                {/*                await TB.sysAlert('talent.resume_imported_success')*/}
                {/*            }*/}
                {/*        }}*/}
                {/*        disabled={isDisabled}*/}
                {/*    />*/}

                {/*</Modal>*/}
            </div>
        </>
    );
}

export default TalentApplication